/**
 * Moon Energy — Cosmic Background Generator
 * Ports the DreamScapeViewer React component to vanilla JS
 *
 * Reads data attributes from #cosmic-background:
 *   data-show-planets="false"  → skips planets / moon / UFO / wormhole
 *   data-theme="supernova"     → adds supernova gradient orbs instead of planet set
 */
(function () {
  'use strict';

  // ---- Moon Phase ----
  function getMoonPhase() {
    const phase = ((Date.now() / 86400000 + 2440587.5 - 2451550.1) / 29.53058867) % 1;
    return phase < 0 ? phase + 1 : phase;
  }

  const CRATERS = [
    { dx: -0.22, dy: -0.20, r: 0.16 },
    { dx:  0.10, dy:  0.22, r: 0.22 },
    { dx:  0.32, dy: -0.28, r: 0.09 },
    { dx: -0.36, dy:  0.14, r: 0.11 },
    { dx:  0.28, dy:  0.10, r: 0.07 },
    { dx: -0.08, dy: -0.38, r: 0.08 },
    { dx:  0.18, dy: -0.08, r: 0.06 },
    { dx: -0.14, dy:  0.34, r: 0.07 },
    { dx:  0.38, dy:  0.30, r: 0.05 },
  ];

  function buildLitPath(ctx, cx, cy, r, phase, cos_ph, rx) {
    ctx.beginPath();
    if (phase <= 0.5) {
      ctx.arc(cx, cy, r, -Math.PI / 2, Math.PI / 2, false);
      if (cos_ph >= 0) {
        ctx.ellipse(cx, cy, rx, r, 0, Math.PI / 2, -Math.PI / 2, true);
      } else {
        ctx.ellipse(cx, cy, rx, r, 0, Math.PI / 2, -Math.PI / 2, false);
      }
    } else {
      ctx.arc(cx, cy, r, -Math.PI / 2, Math.PI / 2, true);
      if (cos_ph <= 0) {
        ctx.ellipse(cx, cy, rx, r, 0, Math.PI / 2, -Math.PI / 2, true);
      } else {
        ctx.ellipse(cx, cy, rx, r, 0, Math.PI / 2, -Math.PI / 2, false);
      }
    }
  }

  function drawMoonPhase(canvas, phase) {
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    const s = canvas.width / dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);
    const cx = s / 2, cy = s / 2, r = s / 2 - 1;

    const phAngle = phase * 2 * Math.PI;
    const cos_ph = Math.cos(phAngle);
    const rx = Math.abs(cos_ph) * r;

    const grad = ctx.createRadialGradient(cx - r * 0.20, cy - r * 0.20, r * 0.05, cx, cy, r);
    grad.addColorStop(0,   '#fffacd');
    grad.addColorStop(0.5, '#f5f5dc');
    grad.addColorStop(1,   '#daa520');

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();

    ctx.save();
    buildLitPath(ctx, cx, cy, r, phase, cos_ph, rx);
    ctx.clip();

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);

    for (const c of CRATERS) {
      const ccx = cx + c.dx * r * 2;
      const ccy = cy + c.dy * r * 2;
      const cr  = c.r * r;
      ctx.fillStyle = 'rgba(172, 168, 152, 0.42)';
      ctx.beginPath();
      ctx.arc(ccx, ccy, cr, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();

    ctx.strokeStyle = 'rgba(210, 206, 195, 0.18)';
    ctx.lineWidth   = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, r - 1, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
    ctx.restore();
  }

  const container = document.getElementById('cosmic-background');
  if (!container) return;

  // --- iOS Safari edge-to-edge fix ---
  // On iOS Safari the floating toolbar shrinks window.innerHeight / 100dvh,
  // but window.screen.height stays constant — use it so stars fill the area
  // behind the address bar. On all other browsers (desktop Chrome, Firefox,
  // Edge, desktop Safari) the CSS 100lvh is accurate; applying screen.height
  // there would inflate the container beyond the viewport and shift all
  // percentage-based planet positions downward, so we skip it.
  var isIosSafari = /iP(hone|od|ad)/.test(navigator.userAgent) &&
    /WebKit/.test(navigator.userAgent) &&
    !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);

  function fitBgToScreen() {
    var h = window.screen.height;
    container.style.height = h + 'px';
  }
  if (isIosSafari) {
    fitBgToScreen();
    window.addEventListener('orientationchange', function () {
      // Small delay so screen dimensions have updated after rotation.
      setTimeout(fitBgToScreen, 200);
    }, { passive: true });
  }

  const showPlanets = container.dataset.showPlanets !== 'false';
  const theme = container.dataset.theme || 'night-forest';

  // ---- Helpers ----
  function rand(min, max) { return Math.random() * (max - min) + min; }
  function el(tag, cls, styles, attrs) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (styles) Object.assign(e.style, styles);
    if (attrs) Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
    return e;
  }

  // ---- Stars (150) — always shown ----
  // 5 size tiers, varied opacity and twinkle timing so stars don't pulse in sync
  const STAR_TIERS = [
    { count: 36, minSize: 0.5, maxSize: 0.9, minOpacity: 0.3, maxOpacity: 0.6, minDur: 3.5, maxDur: 5.0 },
    { count: 42, minSize: 1.0, maxSize: 1.4, minOpacity: 0.4, maxOpacity: 0.75, minDur: 2.5, maxDur: 4.5 },
    { count: 36, minSize: 1.5, maxSize: 1.9, minOpacity: 0.5, maxOpacity: 0.85, minDur: 1.8, maxDur: 3.5 },
    { count: 24, minSize: 2.0, maxSize: 2.4, minOpacity: 0.6, maxOpacity: 0.9,  minDur: 1.5, maxDur: 3.0 },
    { count: 12, minSize: 2.5, maxSize: 3.0, minOpacity: 0.7, maxOpacity: 1.0,  minDur: 1.5, maxDur: 2.5 },
  ];
  const starsContainer = el('div', 'stars-container');
  STAR_TIERS.forEach(function (tier) {
    for (let i = 0; i < tier.count; i++) {
      const size = rand(tier.minSize, tier.maxSize);
      const opacity = rand(tier.minOpacity, tier.maxOpacity);
      const dur = rand(tier.minDur, tier.maxDur);
      const delay = rand(0, 6);
      const star = el('div', 'star', {
        left: rand(0, 100) + '%',
        top: rand(0, 100) + '%',
        width: size + 'px',
        height: size + 'px',
        opacity: opacity,
        animationDelay: delay + 's',
        animationDuration: dur + 's'
      });
      starsContainer.appendChild(star);
    }
  });
  container.appendChild(starsContainer);

  // ---- Supernova theme: colored orbs only ----
  if (theme === 'supernova') {
    const supernovaOrbs = [
      { color: 'rgba(236,72,153,0.3)', size: 300, x: '20%', y: '30%', delay: '0s' },
      { color: 'rgba(139,92,246,0.25)', size: 400, x: '60%', y: '20%', delay: '2s' },
      { color: 'rgba(59,130,246,0.2)', size: 350, x: '40%', y: '60%', delay: '4s' },
      { color: 'rgba(236,72,153,0.2)', size: 250, x: '75%', y: '70%', delay: '1s' },
      { color: 'rgba(139,92,246,0.15)', size: 200, x: '10%', y: '70%', delay: '3s' },
    ];
    const orbsContainer = el('div', 'orbs-container');
    supernovaOrbs.forEach(o => {
      const orb = el('div', 'orb', {
        left: o.x, top: o.y,
        width: o.size + 'px', height: o.size + 'px',
        background: 'radial-gradient(circle, ' + o.color + ' 0%, transparent 70%)',
        animationDelay: o.delay
      });
      orbsContainer.appendChild(orb);
    });
    container.appendChild(orbsContainer);
    // shooting star still looks great on supernova pages
    const shootingStarsContainer = el('div', 'shooting-stars');
    const shootingStar = el('div', 'shooting-star', {
      top: rand(5, 50) + '%',
      left: rand(5, 60) + '%',
      animationDelay: '0s'
    });
    shootingStarsContainer.appendChild(shootingStar);
    container.appendChild(shootingStarsContainer);
    return;
  }

  // ---- Night Forest theme (default) ----

  // Orbs (5 purple) — always shown on non-supernova pages
  const orbsContainer = el('div', 'orbs-container');
  for (let i = 0; i < 5; i++) {
    const size = rand(20, 60);
    const orb = el('div', 'orb', {
      left: rand(15, 85) + '%',
      top: rand(20, 80) + '%',
      width: size + 'px',
      height: size + 'px',
      animationDelay: rand(0, 4) + 's'
    });
    orbsContainer.appendChild(orb);
  }
  container.appendChild(orbsContainer);

  // ---- Planets, Moon, UFO, Wormhole — only for home page ----
  if (showPlanets) {
    const planetsContainer = el('div', 'planets-container');

    // Saturn
    const saturn = el('div', 'planet with-ring saturn', {
      width: '80px', height: '80px', top: '43%', left: '30%',
      background: 'radial-gradient(circle at 30% 30%, #f0e0b8 0%, #d4b070 50%, #a08050 100%)'
    });
    saturn.appendChild(el('div', 'saturn-bands'));
    saturn.appendChild(el('div', 'planet-ring'));
    planetsContainer.appendChild(saturn);

    // Jupiter (with orange/brown bands + Great Red Spot)
    const jupiter = el('div', 'planet jupiter-planet', {
      width: '100px', height: '100px', top: '70%', right: '35%',
      background: 'radial-gradient(circle at 30% 30%, #e8924a 0%, #c1551e 100%)',
      animationDelay: '2s',
      overflow: 'hidden'
    });
    const jupBands = el('div', 'jupiter-bands');
    jupiter.appendChild(jupBands);
    const jupSpot = el('div', 'jupiter-spot');
    jupiter.appendChild(jupSpot);
    planetsContainer.appendChild(jupiter);

    // Mars
    const mars = el('div', 'planet mars', {
      width: '40px', height: '40px', top: '55%', right: '25%',
      background: 'radial-gradient(circle at 30% 30%, #c1440e 0%, #8b3a0a 100%)',
      animationDelay: '1s'
    });
    planetsContainer.appendChild(mars);

    // Neptune
    const neptune = el('div', 'planet', {
      width: '55px', height: '55px', top: '75%', left: '18%',
      background: 'radial-gradient(circle at 30% 30%, #5b92e5 0%, #3d6db5 100%)',
      animationDelay: '3s'
    });
    planetsContainer.appendChild(neptune);

    // Venus
    const venus = el('div', 'planet', {
      width: '35px', height: '35px', top: '20%', right: '35%',
      background: 'radial-gradient(circle at 30% 30%, #e6d4a3 0%, #c4b48a 100%)',
      animationDelay: '4s'
    });
    planetsContainer.appendChild(venus);

    // Earth
    const earth = el('div', 'planet earth', {
      width: '50px', height: '50px', top: '70%', left: '40%',
      background: 'radial-gradient(circle at 30% 30%, #4fc3f7 0%, #29b6f6 30%, #1a8a5c 50%, #0d5c3f 70%, #1565c0 100%)',
      animationDelay: '1.5s'
    });
    earth.appendChild(el('div', 'earth-continents'));
    planetsContainer.appendChild(earth);

    container.appendChild(planetsContainer);

    // Moon — real phase canvas, click to toggle classic/phase
    const moonPhase = getMoonPhase();
    let moonMode = 'phase';
    const moonCssSize = 140;
    const moonDpr = window.devicePixelRatio || 1;
    const moonCanvas = document.createElement('canvas');
    moonCanvas.width = moonCssSize * moonDpr;
    moonCanvas.height = moonCssSize * moonDpr;
    moonCanvas.style.width = moonCssSize + 'px';
    moonCanvas.style.height = moonCssSize + 'px';
    moonCanvas.className = 'moon main-moon main-moon--phase';
    moonCanvas.setAttribute('role', 'button');
    moonCanvas.setAttribute('tabindex', '0');
    moonCanvas.setAttribute('aria-label', 'Toggle moon appearance');
    function applyMoonGlow(canvas, phase) {
      const glowScale = Math.sin(phase * Math.PI);
      if (glowScale < 0.05) {
        canvas.style.animation = 'none';
        canvas.style.filter = 'none';
      } else {
        canvas.style.animation = '';
        canvas.style.filter = '';
      }
    }
    drawMoonPhase(moonCanvas, moonPhase);
    applyMoonGlow(moonCanvas, moonPhase);
    moonCanvas.addEventListener('click', function () {
      moonMode = moonMode === 'phase' ? 'classic' : 'phase';
      const p = moonMode === 'classic' ? 0.5 : moonPhase;
      drawMoonPhase(moonCanvas, p);
      applyMoonGlow(moonCanvas, p);
    });
    moonCanvas.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        moonMode = moonMode === 'phase' ? 'classic' : 'phase';
        const p = moonMode === 'classic' ? 0.5 : moonPhase;
        drawMoonPhase(moonCanvas, p);
        applyMoonGlow(moonCanvas, p);
      }
    });
    container.appendChild(moonCanvas);

    // UFO + Wormhole
    const ufoContainer = el('div', 'ufo-container');
    ufoContainer.appendChild(el('div', 'wormhole'));
    const ufo = el('div', 'ufo');
    ufo.appendChild(el('div', 'ufo-body'));
    ufo.appendChild(el('div', 'ufo-dome'));
    ufo.appendChild(el('div', 'ufo-light'));
    ufoContainer.appendChild(ufo);
    container.appendChild(ufoContainer);

    // On the single-page hash-routed home template, this script can finish
    // building planets/moon/UFO *after* the page's own hash-routing script
    // already ran on load. If the page loaded directly on a non-home section
    // (e.g. #contact, #faq, #about, #lookbook), hide them immediately so they
    // don't flash in — matching the React app, which only shows planets on Home.
    var initialHash = (window.location.hash || '').replace('#', '').toLowerCase();
    if (initialHash && initialHash !== 'home') {
      planetsContainer.style.display = 'none';
      moonCanvas.style.display = 'none';
      ufoContainer.style.display = 'none';
    }
  }

  // ---- Fireflies (15) — always on night-forest ----
  const firefliesContainer = el('div', 'fireflies-container');
  for (let i = 0; i < 15; i++) {
    const f = el('div', 'firefly', {
      left: rand(0, 100) + '%',
      top: rand(0, 100) + '%',
      animationDelay: rand(0, 10) + 's',
      animationDuration: (20 + rand(0, 15)) + 's'
    });
    firefliesContainer.appendChild(f);
  }
  container.appendChild(firefliesContainer);

  // ---- Shooting Star ----
  const shootingStarsContainer = el('div', 'shooting-stars');
  const shootingStar = el('div', 'shooting-star', {
    top: rand(5, 50) + '%',
    left: rand(5, 60) + '%',
    animationDelay: '0s'
  });
  shootingStarsContainer.appendChild(shootingStar);
  container.appendChild(shootingStarsContainer);

})();
